import {Component, effect, input, output} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from '@angular/forms';
import {ProductDetails, ProductFrequency, ProductType, UnitOfMeasureCode} from '../../../../../api';
import {TranslatePipe} from '@ngx-translate/core';
import {TextInput} from '../../../../components/text-input/text-input';
import {TextArea} from '../../../../components/text-area/text-area';
import {NumericInput} from '../../../../components/numeric-input/numeric-input';
import {ProductTypeDropdown} from '../product-type-dropdown/product-type-dropdown';
import {ProductFrequencyDropdown} from '../product-frequency-dropdown/product-frequency-dropdown';
import {LicencePlanDropdown} from '../licence-plan-dropdown/licence-plan-dropdown';
import {FeatureDropdown} from '../feature-dropdown/feature-dropdown';
import {PluginDropdown} from '../plugin-dropdown/plugin-dropdown';
import {ProductFormValue} from '../../models/product-form-value';
import {LegalEntityDropdown} from '../../../finances/components/legal-entity-dropdown/legal-entity-dropdown';
import {
  AccountingAccountDropdown
} from '../../../settings/components/accounting-settings/components/accounting-accounts/components/accounting-account-dropdown/accounting-account-dropdown';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {Divider} from 'primeng/divider';
import {UnitOfMeasureDropdown} from '../unit-of-measure-dropdown/unit-of-measure-dropdown';
import {ElciaDatePicker} from '../../../../components/date-picker/date-picker';
import {Label} from '../../../../components/label/label';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';

interface LegalEntityProductFormModel {
  legalEntityId: FormControl<number | null>;
  accountingAccountId: FormControl<number | null>;
}

interface PricePeriodFormModel {
  startDate: FormControl<Date | null>;
  endDate: FormControl<Date | null>;
  price: FormControl<number | null>;
}

@Component({
  selector: 'elcia-product-form',
  imports: [ReactiveFormsModule, TranslatePipe, TextInput, TextArea, NumericInput, ProductTypeDropdown, ProductFrequencyDropdown, LicencePlanDropdown, FeatureDropdown, PluginDropdown, LegalEntityDropdown, AccountingAccountDropdown, ElciaButton, Divider, UnitOfMeasureDropdown, ElciaDatePicker, Label],
  templateUrl: './product-form.html',
})
export class ProductForm {
  initialValue = input<ProductDetails | undefined>();
  onSubmit = output<ProductFormValue>();

  protected productForm = new FormGroup({
    nameFr: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    nameEn: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    nameNl: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    descriptionFr: new FormControl<string | null>(null),
    descriptionEn: new FormControl<string | null>(null),
    descriptionNl: new FormControl<string | null>(null),
    frequency: new FormControl<ProductFrequency | null>(null),
    unitOfMeasureCode: new FormControl<UnitOfMeasureCode | null>(null, Validators.required),
    productType: new FormControl<ProductType | null>(null, Validators.required),
    licencePlanId: new FormControl<number | null>(null),
    featureId: new FormControl<number | null>(null),
    pluginId: new FormControl<number | null>(null),
    legalEntityProducts: new FormArray<FormGroup<LegalEntityProductFormModel>>([]),
    pricePeriods: new FormArray<FormGroup<PricePeriodFormModel>>([]),
  });

  protected readonly ProductType = ProductType;
  protected readonly productType = toSignal(
    this.productForm.controls.productType.valueChanges,
    {initialValue: this.productForm.controls.productType.value},
  );

  constructor() {
    // Un produit ne référence un plan que s'il est de type LICENCE, une feature que s'il est de type FEATURE.
    this.productForm.controls.productType.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(type => this.onProductTypeChange(type));

    this.productForm.controls.legalEntityProducts.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.ensureTrailingLegalEntityProduct());

    this.productForm.controls.pricePeriods.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.onPricePeriodsChange());

    // Les dates ne sont exigées que pour un produit avec fréquence : tout changement de fréquence relance leur validation.
    this.productForm.controls.frequency.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.revalidateDates());
  }

  onInitialValueChange = effect(() => {
    const val = this.initialValue();
    const legalEntityProducts = this.productForm.controls.legalEntityProducts;
    const pricePeriods = this.productForm.controls.pricePeriods;
    legalEntityProducts.clear({emitEvent: false});
    pricePeriods.clear({emitEvent: false});

    if (!val) {
      this.productForm.reset();
      this.ensureTrailingLegalEntityProduct();
      this.ensureTrailingPricePeriod();
      return;
    }

    this.productForm.patchValue({
      nameFr: val.nameFr,
      nameEn: val.nameEn,
      nameNl: val.nameNl,
      descriptionFr: val.descriptionFr ?? null,
      descriptionEn: val.descriptionEn ?? null,
      descriptionNl: val.descriptionNl ?? null,
      frequency: val.frequency ?? null,
      productType: val.type,
      licencePlanId: val.licencePlanId ?? null,
      featureId: val.featureId ?? null,
      pluginId: val.pluginId ?? null,
      unitOfMeasureCode: val.unitOfMeasureCode,
    });

    // Le patch d'un produit existant ne déclenche pas valueChanges : on applique manuellement l'état des validateurs.
    this.applyTypeValidators(val.type);

    for (const lep of val.legalEntityProducts) {
      legalEntityProducts.push(
        this.createLegalEntityProductForm(lep.legalEntityId, lep.accountingAccountId),
        {emitEvent: false},
      );
    }
    for (const p of val.pricePeriods) {
      pricePeriods.push(
        this.createPricePeriodForm(new Date(p.startDate), p.endDate ? new Date(p.endDate) : null, p.price),
        {emitEvent: false},
      );
    }

    this.ensureTrailingLegalEntityProduct();
    this.ensureTrailingPricePeriod();
  });

  protected removeLegalEntityProduct(index: number): void {
    this.productForm.controls.legalEntityProducts.removeAt(index);
  }

  protected removePricePeriod(index: number): void {
    this.productForm.controls.pricePeriods.removeAt(index);
  }

  public submit(): void {
    if (this.productForm.invalid) {
      this.productForm.markAllAsTouched();
      return;
    }
    const formValue = this.productForm.getRawValue();
    this.onSubmit.emit({
      id: this.initialValue()?.id ?? null,
      nameFr: formValue.nameFr,
      nameEn: formValue.nameEn,
      nameNl: formValue.nameNl,
      descriptionFr: formValue.descriptionFr,
      descriptionEn: formValue.descriptionEn,
      descriptionNl: formValue.descriptionNl,
      frequency: formValue.frequency,
      licencePlanId: formValue.licencePlanId,
      featureId: formValue.featureId,
      pluginId: formValue.pluginId,
      productType: formValue.productType!,
      unitOfMeasureCode: formValue.unitOfMeasureCode!,
      legalEntityProducts: formValue.legalEntityProducts
        .filter(lep => lep.legalEntityId != null && lep.accountingAccountId != null)
        .map(lep => ({
          legalEntityId: lep.legalEntityId!,
          accountingAccountId: lep.accountingAccountId!,
        })),
      pricePeriods: formValue.pricePeriods
        .filter(p => p.startDate != null && p.price != null)
        .map(p => ({
          startDate: p.startDate!,
          endDate: p.endDate ?? null,
          price: p.price!,
        })),
    });
  }

  private onProductTypeChange(type: ProductType | null): void {
    if (type !== ProductType.LICENCE) {
      this.productForm.controls.licencePlanId.setValue(null, {emitEvent: false});
    }
    if (type !== ProductType.FEATURE) {
      this.productForm.controls.featureId.setValue(null, {emitEvent: false});
    }
    if (type !== ProductType.PLUGIN) {
      this.productForm.controls.pluginId.setValue(null, {emitEvent: false});
    }
    this.applyTypeValidators(type);
  }

  // licencePlanId n'est requis que pour un produit LICENCE, featureId que pour un produit FEATURE,
  // pluginId que pour un produit PLUGIN.
  private applyTypeValidators(type: ProductType | null): void {
    const licencePlanId = this.productForm.controls.licencePlanId;
    const featureId = this.productForm.controls.featureId;
    const pluginId = this.productForm.controls.pluginId;

    if (type === ProductType.LICENCE) {
      licencePlanId.setValidators([Validators.required]);
    } else {
      licencePlanId.clearValidators();
    }
    licencePlanId.updateValueAndValidity({emitEvent: false});

    if (type === ProductType.FEATURE) {
      featureId.setValidators([Validators.required]);
    } else {
      featureId.clearValidators();
    }
    featureId.updateValueAndValidity({emitEvent: false});

    if (type === ProductType.PLUGIN) {
      pluginId.setValidators([Validators.required]);
    } else {
      pluginId.clearValidators();
    }
    pluginId.updateValueAndValidity({emitEvent: false});
  }

  private onPricePeriodsChange(): void {
    this.fillPreviousEndDates();
    this.ensureTrailingPricePeriod();
    this.revalidateDates();
  }

  // Les dates ne sont validées que sur les lignes "réelles" (celles qui portent un prix) :
  // changer le prix doit donc relancer la validation des dates correspondantes.
  private revalidateDates(): void {
    for (const period of this.productForm.controls.pricePeriods.controls) {
      period.controls.startDate.updateValueAndValidity({emitEvent: false});
      period.controls.endDate.updateValueAndValidity({emitEvent: false});
    }
  }

  private fillPreviousEndDates(): void {
    const periods = this.productForm.controls.pricePeriods;
    for (let i = 0; i < periods.length - 1; i++) {
      const current = periods.at(i);
      const nextStart = periods.at(i + 1).controls.startDate.value;
      if (!current.controls.endDate.value && nextStart) {
        const dayBefore = new Date(nextStart);
        dayBefore.setDate(dayBefore.getDate() - 1);
        current.controls.endDate.setValue(dayBefore, {emitEvent: false});
      }
    }
  }

  private ensureTrailingLegalEntityProduct(): void {
    const products = this.productForm.controls.legalEntityProducts;
    const last = products.length ? products.at(products.length - 1) : null;
    if (!last || (last.controls.legalEntityId.value != null && last.controls.accountingAccountId.value != null)) {
      products.push(this.createLegalEntityProductForm(), {emitEvent: false});
    }
  }

  private ensureTrailingPricePeriod(): void {
    const periods = this.productForm.controls.pricePeriods;
    const last = periods.length ? periods.at(periods.length - 1) : null;
    if (!last || (last.controls.startDate.value != null && last.controls.price.value != null)) {
      periods.push(this.createPricePeriodForm(), {emitEvent: false});
    }
  }

  private createLegalEntityProductForm(legalEntityId: number | null = null, accountingAccountId: number | null = null) {
    return new FormGroup<LegalEntityProductFormModel>({
      legalEntityId: new FormControl<number | null>(legalEntityId),
      accountingAccountId: new FormControl<number | null>(accountingAccountId),
    });
  }

  private createPricePeriodForm(startDate: Date | null = null, endDate: Date | null = null, price: number | null = null) {
    return new FormGroup<PricePeriodFormModel>({
      startDate: new FormControl<Date | null>(startDate, this.dateRequiredWhenPriced),
      // Pas de validateur sur endDate : les lignes intermédiaires sont auto-remplies par fillPreviousEndDates,
      // et la dernière période tarifée reste ouverte (date de fin vide = prix en cours).
      endDate: new FormControl<Date | null>(endDate),
      price: new FormControl<number | null>(price),
    });
  }

  // Les dates ne sont exigées que pour un produit avec fréquence, et seulement sur une ligne portant un prix
  // (la ligne vide en bas du tableau reste libre).
  private dateRequiredWhenPriced = (control: AbstractControl): ValidationErrors | null => {
    const group = control.parent as FormGroup<PricePeriodFormModel> | null;
    const hasPrice = group?.controls.price.value != null;
    const hasFrequency = this.productForm.controls.frequency.value != null;
    return hasFrequency && hasPrice && control.value == null ? {required: true} : null;
  };
}
